/**
 * A page about deleting your account that you can read without having one.
 *
 * Google Play requires a deletion route reachable from the open web — not
 * behind a login, not inside an app you have to install first — so that
 * someone who has lost their phone, or never wants to open Grow again, can
 * still find out what deleting does and get it done.
 *
 * That is why this route sits outside the Layout: everything under Layout
 * shows the login page when signed out, which would put the explanation
 * behind the very thing the requirement exists to avoid. So the words are
 * always readable, and only the button needs an account.
 *
 * It carries its own Toaster for the same reason — the shared one lives in
 * Layout, and a confirmation that never appears is worse than none.
 *
 * Route: /delete-account, public (outside Layout). Signed out, its button
 * links to /today (the sign-in screen; "/" is the landing page now), which
 * lands on Today after signing in, not back here.
 *
 * Used by: App.tsx. The same DeleteAccountDialog is opened from
 * Profile > Account (AccountCard).
 *
 * @module DeleteAccountPage
 */

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/shared/components/ui/button';
import { Toaster } from '@/shared/components/ui/toaster';
import { useAuth } from '@/shared/hooks/useAuth';
import { useTranslation } from '@/shared/hooks/useTranslation';
import { DeleteAccountDialog } from './components/DeleteAccountDialog';

/** The open page at /delete-account: what deleting does, and the button once signed in. */
export const DeleteAccountPage: React.FC = () => {
  const { reactTranslate: t } = useTranslation();
  const { currentUser, mysqlUser, loading, logout } = useAuth();
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);

  const signedIn = Boolean(currentUser && mysqlUser);

  return (
    <div className="min-h-screen w-full bg-background px-4 py-12">
      <div className="mx-auto max-w-xl space-y-6 text-foreground">
        <h1 className="text-3xl font-bold">{t('profile.account.deletePage.title')}</h1>

        <div className="space-y-4 text-sm leading-relaxed">
          <p>{t('profile.account.deletePage.intro')}</p>
          <p className="font-medium">{t('profile.account.deletePage.whatGoes')}</p>
          <p>{t('profile.account.deletePage.howLong')}</p>
          <p className="text-muted-foreground">{t('profile.account.deletePage.whatStays')}</p>
        </div>

        <div className="rounded-md border p-4">
          {loading && (
            <p className="text-sm text-muted-foreground">{t('sitewide.loading')}</p>
          )}

          {!loading && !signedIn && (
            <div className="space-y-3">
              <p className="text-sm">{t('profile.account.deletePage.signInPrompt')}</p>
              <Button asChild variant="outline">
                <Link to="/today">{t('auth.signInAction')}</Link>
              </Button>
            </div>
          )}

          {!loading && signedIn && (
            <div className="space-y-3">
              <p className="text-sm">
                {t('profile.account.deletePage.signedInAs', {
                  email: mysqlUser?.email ?? currentUser?.email ?? '',
                })}
              </p>
              <div className="flex flex-wrap gap-2">
                <Button variant="destructive" onClick={() => setIsDeleteOpen(true)}>
                  {t('profile.account.deletePage.openDialog')}
                </Button>
                <Button variant="outline" onClick={() => { void logout(); }}>
                  {t('sitewide.logout')}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                {t('profile.account.deletePage.wrongAccount')}
              </p>
            </div>
          )}
        </div>
      </div>

      <DeleteAccountDialog isOpen={isDeleteOpen} onOpenChange={setIsDeleteOpen} />
      <Toaster />
    </div>
  );
};
